import { Divider, Loader, MenuItem, SelectTextField, filledInputClasses, formLabelClasses, styled } from "@tm/components"
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react"
import { CriterionFilters } from "../../../data/model"
import { sortCriteriaFilterByName } from "./helpers"
import { BestsellerCriteria } from "./BestsellerCriteria"

type Props = {
    description: string
    criterionFilters: CriterionFilters[]
    priorityCriterionFilters?: CriterionFilters[]
    onSelectionChange: (criterionFilter?: CriterionFilters) => void
    removeSelectedCriteria: (criterionFilter?: CriterionFilters) => void
    onSelectionOpened: () => void
    loading: boolean
}

const SpecialSelection = styled(SelectTextField)({
    width: "140px",
    [`.${filledInputClasses.root}`]: {
        [`.${filledInputClasses.input}`]: {
            padding: "4px 32px 8px 6px",
        },
    },
    [`.${formLabelClasses.root}`]: {
        zIndex: 0,
    },
})

/**
 * Select component for Criteria, ordered by their name property, disabled selection if loading
 * @param props
 * @returns
 */
export function CriterionSelection(props: Props) {
    const { loading, description, criterionFilters, priorityCriterionFilters, onSelectionChange, removeSelectedCriteria, onSelectionOpened } = props
    const [selectedCriterionValue, setSelectedCriterionValue] = useState<string | undefined>()

    const sortedCriteriaFilters = useMemo(() => {
        return sortCriteriaFilterByName(criterionFilters)
    }, [criterionFilters])

    const sortedPriorityFilters = useMemo(() => {
        return sortCriteriaFilterByName(priorityCriterionFilters)
    }, [priorityCriterionFilters])

    const allFilters = useMemo(() => {
        return [...sortedPriorityFilters, ...sortedCriteriaFilters]
    }, [sortedCriteriaFilters, sortedPriorityFilters])

    const handleChange = useCallback(
        (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const value = allFilters.find((e) => e.valueKey === event.target.value)
            setSelectedCriterionValue(value?.valueKey)
            if (onSelectionChange) {
                onSelectionChange(value)
            }
        },
        [allFilters, onSelectionChange]
    )

    const handleRemoveSelectedCriteria = (value: CriterionFilters) => {
        removeSelectedCriteria(value)
    }

    useEffect(() => {
        setSelectedCriterionValue(allFilters.find((criterion) => criterion.isSelected)?.valueKey)
    }, [allFilters])

    return (
        <SpecialSelection
            style={{ marginTop: 14 }}
            size="small"
            select
            label={description}
            margin="none"
            value={selectedCriterionValue}
            onChange={handleChange}
            SelectProps={{ onOpen: () => onSelectionOpened() }}
        >
            {loading ? (
                <Loader />
            ) : (
                // Don't use a fragment here as it will break the Select component (MUI way of dealing with it)
                [
                    ...sortedPriorityFilters.map((filter) => (
                        <MenuItem key={`priority-${filter.queryStr}`} value={filter.valueKey}>
                            <BestsellerCriteria filter={filter} onRemoveSelectedCriteria={handleRemoveSelectedCriteria} />
                        </MenuItem>
                    )),
                    !!sortedPriorityFilters?.length && <Divider key="divider" variant="fullWidth" />,
                    ...sortedCriteriaFilters.map((filter) => (
                        <MenuItem key={`all-${filter.queryStr}`} value={filter.valueKey}>
                            <BestsellerCriteria filter={filter} onRemoveSelectedCriteria={handleRemoveSelectedCriteria} />
                        </MenuItem>
                    )),
                ]
            )}
        </SpecialSelection>
    )
}
